/******************************************************************************
 * AdminNotificationsToolsTab: push-notification tools for technicians' phones.
 * Endpoints (core):
 *   GET super_admin/notify_tech              -> wake up every enabled phone
 *                                               (triggers them to send logs/backup)
 *   PUT admin/technicians/notify/{techId}    -> send one typed push to a technician
 *   GET admin/technicians                    -> list technicians for the picker
 *****************************************************************************/

/* Imports */
import { useEffect, useState } from "react";
import { Button, Card, CardBody, Select, Option, Typography } from "@material-tailwind/react";
import Select_ from "react-select";
import toast from "react-hot-toast";
import { BellAlertIcon, DevicePhoneMobileIcon } from "@heroicons/react/24/outline";
import { t } from "i18next";
/***/

/* Components */
import WorkorderSearchSelect from "../../components/WorkorderSearchSelect";
/***/

/* Services */
import Loading from "../../../../../../components/loading/Loading";
import { api } from "../../../../../../services/api/api";
import { showApiErrorToast } from "../../../../../../services/api/error";
/***/

/**
 * NOTIFICATION_TYPES: the FCM push types selectable when notifying a technician.
 * Kept in sync with EasyTech.NotificationType (core enum).
 */
const NOTIFICATION_TYPES: EasyTech.NotificationType[] = [
  "REFRESH",
  "GET_WORKORDER",
  "UPLOAD_WORKORDER",
  "UPDATE_STATUS",
  "REMOVE_PENDING_FLAG",
  "SEND_DATABASE",
  "SEND_LOGS",
  "AUTO_DIAG_REMINDER"
];

/**
* AdminNotificationsToolsTab: notification tools
*/
export default function AdminNotificationsToolsTab() {
  const [techs, setTechs] = useState<EasyTech.Technician[] | null>(null);
  const [wakeLoading, setWakeLoading] = useState<boolean>(false);
  const [notifLoading, setNotifLoading] = useState<boolean>(false);
  const [selectedTech, setSelectedTech] = useState<string>("");
  const [selectedType, setSelectedType] = useState<EasyTech.NotificationType>("REFRESH");
  const [workOrderId, setWorkOrderId] = useState<string>("");

  useEffect(() => {
    api.get("admin/technicians")
      .then((ret) => setTechs(ret as EasyTech.Technician[]))
      .catch((err) => {
        console.error(err);
        setTechs([]);
      });
  }, []);

  /**
  * wakeAll: wake up every technician phone (send-logs broadcast)
  */
  const wakeAll = () => {
    setWakeLoading(true);
    api.get("super_admin/notify_tech")
      .then(() => toast.success(t("admin:tools.notifications.broadcast.success")))
      .catch((err) => {
        console.error(err);
        showApiErrorToast(err, { fallbackMessage: t("admin:tools.notifications.broadcast.error") });
      })
      .finally(() => setWakeLoading(false));
  };
  /***/

  /**
  * notifyTechnician: send a typed push to the selected technician
  */
  const notifyTechnician = () => {
    if (!selectedTech) {
      toast.error(t("admin:tools.notifications.technician.noTechnician"));
      return;
    }

    setNotifLoading(true);
    const payload: EasyTech.NotificationData = { type: selectedType };
    if (workOrderId.trim()) payload.workOrderId = workOrderId.trim();

    api.put("admin/technicians/notify/" + selectedTech, payload)
      .then(() => {
        toast.success(t("admin:tools.notifications.technician.success"));
        setWorkOrderId("");
      })
      .catch((err) => {
        console.error(err);
        showApiErrorToast(err, { fallbackMessage: t("admin:tools.notifications.technician.error") });
      })
      .finally(() => setNotifLoading(false));
  };
  /***/

  const techOptions = (techs || []).map((tech) => ({
    value: tech.id,
    label: `${tech.firstName || ""} ${tech.lastName || ""}`.trim() || tech.email || tech.id
  }));

  return (
    <div className="grid gap-6 md:grid-cols-2 my-4">
      {/* Wake up all phones */}
      <Card className="border">
        <CardBody className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <BellAlertIcon className="w-5 text-blue-500" />
            <Typography variant="h6">{t("admin:tools.notifications.broadcast.title")}</Typography>
          </div>
          <Typography variant="small" className="text-gray-600">
            {t("admin:tools.notifications.broadcast.notice")}
          </Typography>
          <div className="flex justify-end mt-auto">
            {wakeLoading ?
              <Loading size="h-5 w-5" />
              :
              <Button color="blue" onClick={wakeAll}>
                {t("admin:tools.notifications.broadcast.button")}
              </Button>}
          </div>
        </CardBody>
      </Card>

      {/* Notify one technician */}
      <Card className="border">
        <CardBody className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <DevicePhoneMobileIcon className="w-5 text-blue-500" />
            <Typography variant="h6">{t("admin:tools.notifications.technician.title")}</Typography>
          </div>
          <Typography variant="small" className="text-gray-600">
            {t("admin:tools.notifications.technician.notice")}
          </Typography>

          {techs === null ?
            <Loading size="h-5 w-5" />
            :
            <>
              <Select_
                placeholder={t("admin:tools.notifications.technician.selectTechnician")}
                options={techOptions}
                value={techOptions.find((o) => o.value === selectedTech) || null}
                onChange={(opt) => setSelectedTech(opt ? opt.value : "")} />

              <Select label={t("admin:tools.notifications.technician.type")}
                labelProps={{ className: "!w-fit !h-fit bg-white px-1" }}
                value={selectedType}
                onChange={(value) => setSelectedType((value as EasyTech.NotificationType) || "REFRESH")}>
                {NOTIFICATION_TYPES.map((type) => (
                  <Option key={type} value={type}>
                    {t("admin:tools.notifications.types." + type)}
                  </Option>
                ))}
              </Select>

              <div className="flex flex-col gap-1">
                <Typography variant="small" className="text-gray-600">
                  {t("admin:tools.notifications.technician.workorderId")}
                </Typography>
                <WorkorderSearchSelect value={workOrderId}
                  onChange={setWorkOrderId}
                  disabled={notifLoading}
                  placeholder={t("admin:tools.workorders.searchPlaceholder")} />
              </div>

              <div className="flex justify-end">
                {notifLoading ?
                  <Loading size="h-5 w-5" />
                  :
                  <Button color="blue" onClick={notifyTechnician}>
                    {t("admin:tools.notifications.technician.button")}
                  </Button>}
              </div>
            </>}
        </CardBody>
      </Card>
    </div>
  );
}
/***/
